import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, ImagePlus, Loader2, Search, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Avatar, Badge } from '@/components/ui/misc';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlay';
import { useEntity } from '@/components/data/value';
import { api } from '@/lib/api';
import { loadEntityList, type EntityKind } from '@/lib/entities';
import { cn } from '@/lib/utils';

export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Searchable record picker (single or multiple) for any entity kind. */
export function EntityPicker({ kind, value, onChange, multiple, placeholder, invalid }: { kind: EntityKind; value: string | string[] | null | undefined; onChange(v: string | string[] | null): void; multiple?: boolean; placeholder?: string; invalid?: boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const debounced = useDebounced(q);
  const results = useQuery({
    queryKey: ['entity-search', kind.kind, kind.search === 'server' ? debounced : ''],
    queryFn: () => loadEntityList(kind, debounced),
    enabled: open,
    staleTime: 60_000,
  });
  const selected = multiple ? ((value as string[] | null) ?? []) : value ? [value as string] : [];
  const items = (results.data ?? []).filter((r) => kind.search === 'server' || !q || `${kind.label(r)} ${kind.subtitle?.(r) ?? ''}`.toLowerCase().includes(q.toLowerCase()));

  const toggle = (id: string) => {
    if (multiple) onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    else {
      onChange(id === value ? null : id);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-invalid={invalid}
          className={cn('flex min-h-9 w-full items-center gap-2 rounded-lg border border-input bg-surface px-3 py-1.5 text-start text-sm hover:border-ring/60 aria-[invalid=true]:border-destructive', open && 'border-ring ring-2 ring-ring/20')}
        >
          <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
            {selected.length === 0 && <span className="text-muted-foreground/70">{placeholder ?? 'Select…'}</span>}
            {selected.map((id) => (
              <Selected key={id} kind={kind} id={id} chip={multiple} onRemove={() => toggle(id)} />
            ))}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[280px] p-0">
        <Command shouldFilter={false} className="flex flex-col">
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="size-4 text-muted-foreground" />
            <Command.Input value={q} onValueChange={setQ} placeholder="Search…" className="h-10 flex-1 bg-transparent text-sm outline-none" />
            {results.isFetching && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          </div>
          <Command.List className="max-h-72 overflow-y-auto p-1">
            <Command.Empty className="px-3 py-6 text-center text-[13px] text-muted-foreground">{results.isLoading ? 'Loading…' : 'No matches'}</Command.Empty>
            {items.map((r) => {
              const id = String(r.id);
              const isOn = selected.includes(id);
              return (
                <Command.Item key={id} value={id} onSelect={() => toggle(id)} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] data-[selected=true]:bg-muted">
                  {kind.kind === 'person' && <Avatar size={26} src={kind.image?.(r)} first={String(r.firstName ?? '')} last={String(r.lastName ?? '')} />}
                  {kind.kind === 'horse' && (kind.image?.(r) ? <img src={kind.image(r)} className="size-[26px] rounded-md object-cover" alt="" /> : <span className="size-[26px] rounded-md bg-muted" />)}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{kind.label(r)}</span>
                    {kind.subtitle?.(r) && <span className="block truncate text-xs text-muted-foreground">{kind.subtitle(r)}</span>}
                  </span>
                  {isOn && <Check className="size-4 text-primary" />}
                </Command.Item>
              );
            })}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Selected({ kind, id, chip, onRemove }: { kind: EntityKind; id: string; chip?: boolean; onRemove(): void }) {
  const { row } = useEntity(kind, id);
  const text = row ? kind.label(row) : id.slice(0, 8);
  if (!chip) return <span className="truncate">{text}</span>;
  return (
    <Badge tone="primary" className="pe-1">
      {text}
      <span
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="rounded-full p-0.5 hover:bg-primary/20"
      >
        <X className="size-3" />
      </span>
    </Badge>
  );
}

interface MediaView {
  id: string;
  url: string;
  thumbUrl: string | null;
  fileName: string;
  mime: string;
}

/** Upload a file with the API's presigned flow (upload-url → PUT → complete) and return its media id. */
export async function uploadFile(file: File, ownerType = 'admin', visibility: 'public' | 'private' = 'private'): Promise<MediaView> {
  const { mediaId, upload } = await api.post<{ mediaId: string; upload: { url: string; method: string; headers: Record<string, string> } }>('admin/media/upload-url', {
    fileName: file.name,
    mime: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    ownerType,
    visibility,
  });
  const put = await fetch(upload.url, { method: upload.method, headers: upload.headers, body: file });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  return api.post<MediaView>('admin/media/complete', { mediaId });
}

export function MediaUpload({ value, onChange, accept = 'image/*', ownerType, visibility = 'public' }: { value: string | null | undefined; onChange(id: string | null): void; accept?: string; ownerType?: string; visibility?: 'public' | 'private' }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const preview = useQuery({ queryKey: ['media', value], queryFn: () => api.get<MediaView>(`admin/media/${value}`), enabled: Boolean(value), staleTime: 30 * 60_000 });
  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const media = await uploadFile(file, ownerType, visibility);
      onChange(media.id);
      toast.success('Uploaded');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const isImage = preview.data?.mime?.startsWith('image/') ?? accept.startsWith('image');
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void pick(e.dataTransfer.files[0]);
        }}
        className="group relative grid size-20 shrink-0 place-items-center overflow-hidden rounded-xl border border-dashed border-input bg-muted/40 text-muted-foreground transition-colors hover:border-ring hover:text-foreground"
      >
        {busy ? <Loader2 className="size-5 animate-spin" /> : value && preview.data && isImage ? <img src={preview.data.thumbUrl ?? preview.data.url} className="size-full object-cover" alt="" /> : <ImagePlus className="size-5" />}
      </button>
      <div className="min-w-0 text-[13px]">
        <button type="button" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline" onClick={() => input.current?.click()}>
          <Upload className="size-3.5" /> {value ? 'Replace file' : 'Upload file'}
        </button>
        <p className="truncate text-xs text-muted-foreground">{value ? preview.data?.fileName ?? 'Uploaded' : 'Drag & drop or click'}</p>
        {value && (
          <button type="button" className="text-xs text-destructive hover:underline" onClick={() => onChange(null)}>
            Remove
          </button>
        )}
      </div>
      <input ref={input} type="file" accept={accept} className="hidden" onChange={(e) => void pick(e.target.files?.[0])} />
    </div>
  );
}
